import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { useGetCurrentGame, useGetCurrentLanguage } from 'src/store/game'
import { useSocialStore } from 'src/store/social'
import { useClientSideTranslation } from 'src/services/i18n'
import { isMarbleGame } from 'src/types/domain/Game'
import { displayAmount, displayMoney, getCurrencyDecimals } from 'src/utils/displayAmount'
import { isBirdieGameType } from 'src/utils/gameTypeUtils'
import { BirdieBallDefs } from 'src/components/BirdieBoard/GolfBall'
import {
  IDLE_LIVE_BOARD,
  birdieOptionKey,
  feedStatusForBet,
  type BirdieLiveBoardView
} from 'src/components/BirdieBoard/birdieFeedVerdict'
import { useBirdieLiveBoard } from 'src/store/birdieLiveBoard'
import type { SocialBet } from 'src/types/domain/SocialBet'
import type { Marble } from 'src/types/domain/Marble'
import HistoryIcon from 'src/app/assets/icons/ico-history.svg'
import AngleRightIcon from 'src/app/assets/icons/ico-angle-right.svg'
import ArrowLeftIcon from 'src/app/assets/icons/ico-arrow-left.svg'
import { calculateMultiplier } from '../utils/calculateMultiplier'
import { COMPACT_THRESHOLD, formatSocialCompact } from '../utils/formatSocialCompact'
import { formatSocialAmount } from '../utils/getSocialCurrencySymbol'
import { PlayerDetails } from '../SocialList/components/PlayerDetails'
import { BetMarbles } from '../SocialList/components/BetMarbles'
import {
  displayStatusForTab,
  isWinningBet,
  type BetMeter,
  type CrashRoundPhase,
  type PillMode,
  type SocialFocus,
  type StakerIdentity
} from './socialPanelLogic'
import type { HeroKind } from './socialRoundLogic'
import { CENTS_THRESHOLD, dropCents } from './dropCents'
import { PlayerAvatar } from './PlayerAvatar'
import { useCountUp } from './useCountUp'

export type SocialPanelTab = 'current' | 'previous'

function getEntryStatusClass(status: SocialBet['status']): string {
  switch (status) {
    case 'won':
      return 'entry--won'
    case 'cashed_out':
      return 'entry--cashed-out'
    case 'lost':
      return 'entry--lost'
    default:
      return ''
  }
}

type BetTableRowProps = Readonly<{
  bet: SocialBet
  marbles: Marble[]
  isCrashGame: boolean
  currencySymbol: string | null
  displayMultiplier: number
  /** The viewer's currency, for per-currency decimal places. */
  currency?: string
  /** True when displayMultiplier is a real conversion (from getSocialUsdRate). */
  converted?: boolean
  /** Which tab the row renders on — the PREVIOUS tab tells the whole truth
   * (a still-'pending' bet from a settled round displays as LOST). */
  tab: SocialPanelTab
  /** The previous round was CANCELLED — its uncashed bets were refunded and
   * display neutral, never LOST (review C3). */
  previousRoundCancelled?: boolean
  /** The CURRENT round's crash is on the viewer's frames (visual-gated) —
   * uncashed rows flip to LOST so it's clear they didn't make it (v24). */
  currentRoundCrashed?: boolean
  /** Row position for the LOST cascade (v29): the bust rolls down the list
   * as a staggered wave instead of every row flipping at once. */
  cascadeIndex?: number
  /** The game is Birdie Time (B22): the picks draw as the board's family icon
   * and balls rather than as marbles. Defaults to false — the marble path. */
  isBirdie?: boolean
  /** B41 — what the Birdie board is drawing, so a row can settle ball by ball
   * without ever leading the video. Idle on every other game, which makes the
   * verdict a no-op and leaves those rows exactly as they were. */
  liveBoard?: BirdieLiveBoardView
}>

function BetTableRow({
  bet,
  marbles,
  isCrashGame,
  currencySymbol,
  displayMultiplier,
  currency,
  converted = false,
  tab,
  previousRoundCancelled = false,
  currentRoundCrashed = false,
  cascadeIndex = 0,
  isBirdie = false,
  liveBoard = IDLE_LIVE_BOARD
}: BetTableRowProps) {
  const game = useGetCurrentGame()
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)

  const selectedItems =
    bet.betDetails?.selectedItems ||
    ('selected_items' in (bet.betDetails || {}) ? (bet.betDetails as any).selected_items : []) ||
    []
  const selectedMarbles = selectedItems
    .map((marbleId: string) => marbles.find((m) => m.uuid === marbleId))
    .filter((m): m is Marble => m !== undefined)

  // Render-time lightning-boost reversal — see BetListItem for the full
  // rationale. The store mutation `applyLightningBoost` may or may not have
  // run for this bet (timing depends on reveal vs. bet arrival order on page
  // reload), so we apply the factor here when needed, gated by `boostedBetIds`
  // to avoid double-applying when the store already contains the boosted value.
  const currentRace = isMarbleGame(game) ? game.races[0] : undefined
  const isBoostedInStore = useSocialStore((s) => Boolean(s.boostedBetIds[bet.id]))
  const lightningFactor = (() => {
    if (isBoostedInStore) return 1
    if (!currentRace) return 1
    if (bet.raceId !== currentRace.uuid) return 1
    if (!currentRace.isLightningRound) return 1
    if (!currentRace.lightningBoostedItems || currentRace.lightningBoostedItems.length === 0) {
      return 1
    }
    if (!currentRace.lightningBoostPercent || currentRace.lightningBoostPercent <= 0) return 1
    const onBoosted = selectedItems.some((m: string) =>
      currentRace.lightningBoostedItems!.includes(m)
    )
    if (!onBoosted) return 1
    return 1 + currentRace.lightningBoostPercent / 100
  })()
  const displayedPayout = bet.potentialPayout * lightningFactor

  const multiplier = calculateMultiplier(bet.betAmount, displayedPayout)

  // Big sums crowd this row (picks + badge + stake + potential win), so whole
  // units only above the threshold. TRUNCATED, never rounded. Crash opts out
  // via Infinity: its row is sparser and reads fine with cents.
  const centsThreshold = isCrashGame ? Infinity : CENTS_THRESHOLD
  const money = (value: number): string => {
    const scaled = value * displayMultiplier
    if (converted && scaled >= COMPACT_THRESHOLD) {
      // Compact output has no cents to drop — and dropCents would cut
      // "1.9m" at its decimal point. Bypass it entirely.
      return formatSocialCompact(scaled)
    }
    // Unconverted (rate 1: USD viewers, crypto phase-1, unknown currency)
    // keeps the LEGACY displayMoney path so output is bit-identical to
    // today — including sub-cent sweeps dust from other operators in the
    // shared feed, which displayMoney's smart decimals expand ("0.0050")
    // and a fixed 2dp would flatten to a false "$0.00".
    const formatted = converted
      ? displayAmount(scaled, true, getCurrencyDecimals(scaled, currency ?? 'USD'))
      : displayMoney(scaled, true)
    // dropCents keys on the CONVERTED value deliberately: its threshold is
    // about rendered row WIDTH, and width is a property of the string on
    // screen (see Global Constraints).
    return dropCents(formatted, scaled, centsThreshold)
  }

  const amount = money(bet.betAmount)

  // B41 — Birdie's outcome resolves AS THE BALLS DROP, so a row need not wait
  // for settlement to tell the truth.
  //
  // ORDER MATTERS. The server's answer first, then the balls, and only then the
  // generic tab rule. That last rule turns every still-pending row LOST the
  // moment the round resolves — which lands BEFORE the operator's payout does —
  // so running it ahead of the live verdict would drag a row this feature had
  // already shown as WON through LOST on its way to being confirmed won. Two
  // flips and a second win flash, which is precisely what a live verdict is not
  // allowed to cost anyone.
  //
  // Every other game is byte-identical here: with no key and an idle board the
  // first call returns the row's own status, and anything but `pending` is what
  // the tab rule would have returned for it anyway.
  const serverOrLive = feedStatusForBet(
    bet.status,
    bet.raceId,
    isBirdie ? birdieOptionKey(selectedItems, marbles) : undefined,
    liveBoard
  )
  const displayStatus =
    serverOrLive === 'pending'
      ? displayStatusForTab(bet.status, tab, previousRoundCancelled, currentRoundCrashed)
      : serverOrLive
  const statusClass = getEntryStatusClass(displayStatus)
  const isWin = isWinningBet(displayStatus)
  const isLost = displayStatus === 'lost'

  // v20 — the bail-out flash: fires ONLY on a live pending→cashed TRANSITION
  // on the CURRENT tab (never on mount, never on the settled ledger — a late
  // outcome correcting a previous-round row is a quiet fix, not a
  // celebration; review C5).
  const prevWinRef = useRef(isWin)
  const [flash, setFlash] = useState(false)
  useEffect(() => {
    const wasWin = prevWinRef.current
    prevWinRef.current = isWin
    if (isWin && !wasWin && tab === 'current') {
      setFlash(true)
      const timer = setTimeout(() => setFlash(false), 750)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [isWin, tab])

  // A 0 payout means UNKNOWN (a bet cached by an older server), exactly as the
  // payout cell treats it — so suppress the odds badge too rather than
  // advertising a literal `x0`.
  const hasKnownPayout = displayedPayout > 0
  const cashoutLabel = isWin
    ? isCrashGame && bet.cashoutMultiplier
      ? `${bet.cashoutMultiplier.toFixed(2)}x`
      : hasKnownPayout
        ? `x${multiplier}`
        : ''
    : !isLost && !isCrashGame && hasKnownPayout
      ? `x${multiplier}`
      : ''

  return (
    <div
      className={`social-bet-table__entry ${statusClass} ${flash ? 'entry--flash' : ''}`}
      style={{ '--cascade-i': cascadeIndex } as React.CSSProperties}
    >
      {/* Line 1: avatar + player (left) · picks + cashout multiplier (right) */}
      <div className="social-bet-table__entry-top">
        <span aria-hidden="true" className="social-bet-table__entry-avatar">
          <PlayerAvatar country={bet.playerCountry} name={bet.playerDisplayName} />
        </span>
        <span className="social-bet-table__entry-player">
          <PlayerDetails country={bet.playerCountry} name={bet.playerDisplayName} />
        </span>
        <span className="social-bet-table__entry-top-right">
          {!isCrashGame && selectedMarbles.length > 0 && (
            <span className="social-bet-table__entry-picks">
              <BetMarbles
                bet={bet}
                isBirdie={isBirdie}
                selectedItems={selectedItems}
                selectedMarbles={selectedMarbles}
                simple
              />
            </span>
          )}
          {/* Crash keeps the multiplier up here — it has no picks, so nothing
              competes for the corner. On every other game the picks own this
              spot and the multiplier moves to the bottom line, otherwise a
              3-marble tricast and a 1-marble pick push the badge to different
              x-positions and the column visibly dances row to row. */}
          {cashoutLabel && isCrashGame && (
            <span className="social-bet-table__entry-mult">{cashoutLabel}</span>
          )}
        </span>
      </div>
      {/* Line 2: stake (left) + payout (right) */}
      <div className="social-bet-table__entry-bottom">
        <span className="social-bet-table__entry-amount">
          {currencySymbol}
          {amount}
        </span>
        {cashoutLabel && !isCrashGame && (
          <span className="social-bet-table__entry-mult">{cashoutLabel}</span>
        )}
        <span
          className={`social-bet-table__entry-payout ${isWin ? 'entry-payout--won' : ''} ${isLost ? 'entry-payout--lost' : ''} ${
            !isWin && !isLost && !isCrashGame && displayedPayout > 0
              ? 'entry-payout--potential'
              : ''
          }`}
        >
          {isWin
            ? formatSocialAmount(currencySymbol, money(bet.actualPayout ?? displayedPayout))
            : isLost
              ? t('Lost')
              : // Pending, non-crash: show what this bet stands to WIN, right
                // in the row. It used to be hidden behind a hover overlay, so
                // it was invisible on mobile entirely.
                //
                // Crash is excluded on purpose: its potentialPayout is the
                // operator LIABILITY CEILING, not stake x odds, which is why
                // every other crash surface gates this off too.
                //
                // 0 means UNKNOWN, not "wins nothing" — a bet cached by an
                // older server carries no payout, and asserting $0.00 would be
                // worse than saying nothing.
                !isCrashGame && displayedPayout > 0
                ? formatSocialAmount(currencySymbol, money(displayedPayout))
                : ''}
        </span>
      </div>
    </div>
  )
}

/**
 * The compact live readout the v16 top bar (and the adaptive pill) show: the
 * SAME phase/multiplier/timer the hero shows, sourced from the crash state in
 * the container (getDisplayMultiplier + the pure phase/countdown helpers) and
 * passed as plain props so the harness can screenshot every state.
 */
export type CrashTopBarReadout = Readonly<{
  phase: CrashRoundPhase
  /** Whether the pill/top-bar summarise STAKED (betting/live) or WON (crashed/settled). */
  pillMode: PillMode
  /** Short phase caption, e.g. 'Place your bets', 'Crashed', 'Round won'. */
  phaseLabel: string
  /** The live value line: the climbing multiplier, '3.17x', or a m:ss countdown. */
  valueText: string
  /** data-heat for the state-value — matches the hero's colour ramp (v19). */
  stateHeat: string
}>

/**
 * The BAIL-OUT METER band (v22) — the panel's signature element, one story:
 * TOTAL WON, always. Left: the round's biggest stakes as generated avatar
 * discs, the n/N cashed tally, and a progress track that fills as players
 * bail. Right: total won counting up from 0 with every cashout, frozen on
 * the viewer's crash. On the PREVIOUS tab a dedicated line shows what that
 * round crashed at, in the shared history-pill tier colours. Pure props;
 * the count-up is the only state.
 */
function BetMeterBand({
  meter,
  currencySymbol,
  fmt,
  t,
  snapKey = '',
  crashLine = null,
  onNavPrevious = null,
  onNavBack = null,
  // Defaults reproduce crash EXACTLY: its hero is always money won, and its
  // fill always means cashed-out players.
  heroKind = 'won',
  showProgress = true
}: Readonly<{
  meter: BetMeter
  currencySymbol: string | null
  fmt: (value: number) => string
  t: (key: string) => string
  /** Context identity — a change RE-ROLLS the hero from zero (tab switch). */
  snapKey?: string
  /** The dedicated "crashed at" line (previous tab): the multiplier + its
   * history-pill tier class. Null hides the whole line. */
  crashLine?: { multiplier: number; tierClass: string } | null
  /** v25 mobile — the tab rail folds INTO the band: far-right affordance to
   * the previous round (history icon + chevron). Hidden on desktop (CSS). */
  onNavPrevious?: (() => void) | null
  /** v25 mobile — on the previous tab, the far-left affordance back to the
   * live All-bets list (flipped chevron + live dot). Hidden on desktop. */
  onNavBack?: (() => void) | null
  /**
   * What the hero number MEANS, so the caption can never describe a different
   * figure than the one shown. Defaults to 'won', which is crash's behaviour
   * verbatim: crash players cash out live, so its hero is money won in every
   * phase and "Total won" is true throughout.
   *
   * Games with no mid-round cashout pass 'staked' while the round runs —
   * nothing can be won yet — and 'won' once it settles.
   */
  heroKind?: HeroKind
  /**
   * Whether the progress fill means anything yet. Crash fills it with cashed
   * players as the round runs. A marble round has nothing to fill until it
   * settles, so a creeping bar would assert something untrue.
   */
  showProgress?: boolean
}>) {
  const shownHero = useCountUp(meter.heroValue, 450, snapKey)
  const heroText = fmt(shownHero)
  // No-truncate contract: a huge win total steps the font DOWN, never clips
  // (the house pattern — moneyLenClass on every other crash money surface).
  // A ZERO total mutes instead of glowing (a dead previous round, or the
  // fresh round before its first bail-out — the green IGNITES with the
  // first real money).
  const heroSize =
    (heroText.length >= 14
      ? ' social-bet-table__meter-hero--huge'
      : heroText.length >= 11
        ? ' social-bet-table__meter-hero--long'
        : '') + (meter.heroValue === 0 ? ' social-bet-table__meter-hero--zero' : '')
  return (
    <div className={`social-bet-table__meter social-bet-table__meter--${meter.mode}`}>
      <div className="social-bet-table__meter-body">
        {onNavBack && (
          <button
            aria-label={t('All bets')}
            className="social-bet-table__meter-nav social-bet-table__meter-nav--back"
            onClick={onNavBack}
            type="button"
          >
            <AngleRightIcon className="social-bet-table__meter-nav-chevron social-bet-table__meter-nav-chevron--flip" />
          </button>
        )}
        <div
          className={`social-bet-table__meter-left${
            showProgress ? '' : ' social-bet-table__meter-left--no-progress'
          }`}
        >
          {/* v32 — no crowd cluster in the band (Kasper: keep it simple,
              just "3 / 8 Bets"); the collapsed mobile pill keeps its gems. */}
          <span className="social-bet-table__meter-crowd">
            <span className="social-bet-table__meter-tally">
              <b>{meter.cashedCount}</b>
              <span className="social-bet-table__meter-tally-sep">/</span>
              {meter.totalCount} {t('Bets')}
            </span>
          </span>
          <span
            aria-valuemax={meter.totalCount}
            aria-valuemin={0}
            aria-valuenow={meter.cashedCount}
            className={`social-bet-table__meter-track${
              meter.mode === 'wins' && meter.cashedCount === 0
                ? ' social-bet-table__meter-track--idle'
                : ''
            }`}
            role="progressbar"
          >
            <span
              className="social-bet-table__meter-fill"
              style={{ width: showProgress ? `${Math.round(meter.progress * 1000) / 10}%` : '0%' }}
            />
          </span>
        </div>
        <div className="social-bet-table__meter-right">
          <span className={`social-bet-table__meter-hero${heroSize}`}>
            {currencySymbol}
            {heroText}
          </span>
          {/* Derived from the SAME readout the hero came from, so the caption
              can never describe a different number than the one displayed.
              Two literal t() calls so the i18n extractor sees both keys. */}
          <span className="social-bet-table__meter-caption">
            {heroKind === 'won' ? t('Total won') : t('Total')}
          </span>
        </div>
        {onNavPrevious && (
          <button
            aria-label={t('Previous round')}
            className="social-bet-table__meter-nav social-bet-table__meter-nav--next"
            onClick={onNavPrevious}
            type="button"
          >
            <AngleRightIcon className="social-bet-table__meter-nav-chevron" />
          </button>
        )}
      </div>
      {crashLine && (
        <div className="social-bet-table__meter-crashline">
          <span className="social-bet-table__meter-crashline-label">{t('Last round')}</span>
          {/* A decorative button — the shared history-pill tier styles in
              marbles.scss are `button`-scoped, and the crashpill rules below
              neutralise the interactive affordances (cursor/hover/active). */}
          <button
            className={`crash-multiplier-pill social-bet-table__meter-crashpill ${crashLine.tierClass}`}
            disabled
            tabIndex={-1}
            type="button"
          >
            <span>{crashLine.multiplier.toFixed(2)}x</span>
          </button>
        </div>
      )}
    </div>
  )
}

export type SocialPanelViewProps = Readonly<{
  focus: SocialFocus
  activeTab: SocialPanelTab
  isCollapsed: boolean
  sortedBets: SocialBet[]
  marbles: Marble[]
  isCrashGame: boolean
  currencySymbol: string | null
  displayMultiplier: number
  /** The viewer's currency, for per-currency decimal places. */
  currency?: string
  /** True when displayMultiplier is a real conversion (from getSocialUsdRate). */
  converted?: boolean
  currentCount: number
  previousCount: number
  currentTotal: number
  previousWonTotal: number
  onTabClick: (tab: SocialPanelTab) => void
  onOpenSocial: () => void
  onCloseSocial: () => void
  // v20 — the bail-out meter for the ACTIVE tab (current: live figures;
  // previous: the settled round frozen). Optional so older callers/tests
  // render unchanged; the crash container always supplies it.
  meter?: BetMeter
  /** The ACTIVE TAB's biggest stakes (identity for the meter crowd's discs). */
  stakers?: StakerIdentity[]
  /** The CURRENT round's biggest stakes — the collapsed pill's crowd (v25).
   * The pill always tells the current round's story even when the open panel
   * was parked on the previous tab. Falls back to `stakers` when absent
   * (older callers/harness). */
  currentStakers?: StakerIdentity[]
  /** The previous round's crash multiplier — its own "Crashed at" line on
   * the previous tab. */
  previousCrashMultiplier?: number | null
  /** History-pill tier class for the previous round's crash (colour). */
  previousCrashTier?: string | null
  /** The previous round was cancelled — refunded bets display neutral. */
  previousRoundCancelled?: boolean
  /** The current round's crash is on the viewer's frames (visual-gated) —
   * flips uncashed current-tab rows to LOST (v24). */
  currentRoundCrashed?: boolean
  /** Identity of the previous round shown on the previous tab — part of the
   * meter's context key so a rotation re-presents (re-rolls) the total. */
  previousRoundKey?: string
  // v16 — the crash top-bar / adaptive-pill readout. Optional so non-crash
  // callers and older tests render unchanged; the crash container always
  // supplies it.
  topBar?: CrashTopBarReadout
  /** Total won this round — shown by the pill/top-bar once crashed/settled. */
  currentWonTotal?: number
  /** Winners this round — the count shown in won mode. */
  winnersCount?: number
  // v18 — a ref to the live state-value span so the container can drive it at
  // the HERO's rAF cadence (imperative textContent writes, zero re-renders),
  // keeping the social readout climbing as smoothly as the hero. The initial
  // paint still uses topBar.valueText (already floored) so the value never
  // blanks; the rAF only refreshes it while live+social.
  stateValueRef?: RefObject<HTMLSpanElement>
  /**
   * What the meter's hero number means, so the caption follows the value.
   * Defaults to 'won' — crash verbatim, whose hero is money won in every
   * phase because players cash out live.
   */
  heroKind?: HeroKind
  /**
   * Whether the progress fill means anything yet. Defaults to true (crash
   * fills it with cashed-out players); games with no mid-round cashout pass
   * false until the round settles.
   */
  showProgress?: boolean
  /**
   * The money the COLLAPSED mobile pill shows. Always the CURRENT round, even
   * when the open panel is parked on the previous tab.
   *
   * Defaults to `currentWonTotal` — crash verbatim, whose pill always tells
   * the won story. Games with no mid-round cashout pass their stake here while
   * the round runs, otherwise the pill sat at $0.00 for the whole round.
   */
  pillHeroValue?: number
  /**
   * What `pillHeroValue` means, so the pill's LABEL and COUNT follow its own
   * money instead of the crash-only top bar. Crash keeps supplying `topBar`
   * and is unaffected.
   */
  pillHeroKind?: HeroKind
}>

/**
 * Presentational shell for the crash social bets panel. Pure props → markup;
 * the store/i18n wiring lives in SocialBetTable, and the dev harness renders
 * this directly with crafted props (deterministic screenshots of every
 * focus). Money is formatted here from backend bet fields only.
 */
export function SocialBetPanelView({
  focus,
  activeTab,
  isCollapsed,
  sortedBets,
  marbles,
  isCrashGame,
  currencySymbol,
  displayMultiplier,
  currency,
  converted = false,
  currentCount,
  previousCount,
  currentTotal,
  previousWonTotal,
  onTabClick,
  onOpenSocial,
  onCloseSocial,
  meter,
  stakers = [],
  currentStakers,
  previousCrashMultiplier = null,
  previousCrashTier = null,
  previousRoundCancelled = false,
  currentRoundCrashed = false,
  previousRoundKey = '',
  topBar,
  currentWonTotal = 0,
  winnersCount = 0,
  stateValueRef,
  heroKind = 'won',
  showProgress = true,
  pillHeroValue,
  pillHeroKind
}: SocialPanelViewProps) {
  const currentLanguage = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLanguage)

  // B22 — Birdie's picks draw in the board's own vocabulary (family icon +
  // balls, `<BirdieFeedPick>`) instead of as marble tokens. ONE predicate,
  // read once here and handed down as a plain prop, so `<BetMarbles>` stays
  // pure and every other game takes its existing path untouched.
  const game = useGetCurrentGame()
  const isBirdie = isMarbleGame(game) && isBirdieGameType(game.track.gameType)
  // B41 — read ONCE here for the same reason `isBirdie` is: one subscription
  // for the whole list rather than one per row, and the rows stay pure. Idle
  // (and never written) on every game whose board does not publish.
  const liveBoard = useBirdieLiveBoard()

  // The meter hero and the collapsed pill keep their CENTS on every game. The
  // band has room for them and the full figure reads better — the cents are
  // dropped only in the ROWS, where picks + badge + stake + potential win
  // genuinely compete for width.
  const fmt = (value: number): string => {
    const scaled = value * displayMultiplier
    if (converted && scaled >= COMPACT_THRESHOLD) {
      // Even the roomier meter band can't seat "GC40,000,000.00" — same
      // magnitude guard as the rows, converted path only.
      return formatSocialCompact(scaled)
    }
    return converted
      ? displayAmount(scaled, true, getCurrencyDecimals(scaled, currency ?? 'USD'))
      : displayMoney(scaled, true)
  }

  const money = (value: number): ReactNode => (
    <>
      {currencySymbol}
      {fmt(value)}
    </>
  )

  // v16 — the pill / top-bar summary adapts with the round phase: total
  // STAKED + live count while betting/climbing; total WON + winners once the
  // round crashed/settled (players check who won).
  // Crash drives this from its top bar. Games without one (no mid-round
  // cashout, so no crash phase) drive it from the pill's own hero, otherwise
  // the pill still read "Live bets" after the round had settled.
  const wonMode = topBar ? topBar.pillMode === 'won' : pillHeroKind === 'won'
  const summaryLabel = wonMode ? t('Won this round') : t('Live bets')
  const summaryCount = wonMode ? winnersCount : currentCount
  const summaryTotal = wonMode ? currentWonTotal : currentTotal
  const phaseState = topBar?.phase ?? 'betting'

  // v25 — the mini-meter pill's figures: ALWAYS the current round (the meter
  // prop follows the active tab, which may be parked on 'previous'). Its own
  // count-up context so the round reset snaps to $0 like the meter hero.
  const pillStakers = currentStakers ?? stakers
  const pillProgress = currentCount > 0 ? Math.min(1, Math.max(0, winnersCount / currentCount)) : 0
  // Falls back to currentWonTotal so crash is unchanged.
  const pillShownWon = useCountUp(pillHeroValue ?? currentWonTotal, 450, 'pill')

  // v24 — the meter's context identity: a change RE-ROLLS the count-up from
  // zero up to the new context's total (Kasper note d — switching tabs counts
  // the amount up; never a tween between unrelated rounds' money). Keyed on
  // the TAB (+ the previous round's identity so a rotation while parked on
  // the previous tab re-presents it). Mode flips within a tab are NOT context
  // changes: the crash freeze keeps its value, and the round reset's drop to
  // $0 snaps via the decrease rule.
  const meterSnapKey = activeTab === 'previous' ? `previous:${previousRoundKey}` : 'current'
  // The "Crashed at" line — previous tab only (the current round's crash was
  // already on the stream; a chip there just adds clutter, per Kasper).
  const meterCrashLine =
    activeTab === 'previous' && previousCrashMultiplier != null && previousCrashTier
      ? { multiplier: previousCrashMultiplier, tierClass: previousCrashTier }
      : null

  return (
    <div
      className={`social-bet-table social-bet-table--focus-${focus} social-bet-table--phase-${phaseState}`}
    >
      {/* ── Mobile collapsed PILL (stream focus) — a MINI-METER (v25):
          the current round's top-staker gems, the n/N cashed tally, the
          progress track and the counting total won. Always the CURRENT
          round (never the parked previous tab). Meter-less callers keep
          the old label/count/total content. ── */}
      <button
        aria-label={t('Show live bets')}
        className={`social-bet-table__pill ${wonMode ? 'social-bet-table__pill--won' : ''}`}
        onClick={onOpenSocial}
        type="button"
      >
        {meter ? (
          <>
            {pillStakers.length > 0 && (
              <span aria-hidden="true" className="social-bet-table__pill-crowd">
                {pillStakers.map((staker, index) => (
                  <span
                    className="social-bet-table__pill-gem"
                    key={`${staker.name ?? '?'}-${index}`}
                  >
                    <PlayerAvatar country={staker.country} name={staker.name} />
                  </span>
                ))}
              </span>
            )}
            <span className="social-bet-table__pill-tally">
              <b>{winnersCount}</b>
              <span className="social-bet-table__pill-tally-sep">/</span>
              {currentCount}
            </span>
            <span
              aria-hidden="true"
              className={`social-bet-table__pill-track${
                winnersCount === 0 ? ' social-bet-table__pill-track--idle' : ''
              }`}
            >
              <span
                className="social-bet-table__pill-fill"
                style={{ width: `${Math.round(pillProgress * 1000) / 10}%` }}
              />
            </span>
            <span
              // Keyed on the value the pill actually DISPLAYS, not on the won
              // total. On a game with no mid-round cashout the pill shows the
              // round's STAKE while it runs, and won-total is 0 for that whole
              // time — so a live $1,240 staked was being painted in the
              // "nothing here yet" grey.
              className={`social-bet-table__pill-total social-bet-table__pill-total--won${
                (pillHeroValue ?? currentWonTotal) === 0
                  ? ' social-bet-table__pill-total--zero'
                  : ''
              }`}
            >
              {currencySymbol}
              {fmt(pillShownWon)}
            </span>
            <AngleRightIcon className="social-bet-table__pill-chevron" />
          </>
        ) : (
          <>
            <span className="social-bet-table__pill-live">
              <span className="social-bet-table__live-dot" />
              {summaryLabel}
            </span>
            <span className="social-bet-table__pill-count">{summaryCount}</span>
            <span className="social-bet-table__pill-total">{money(summaryTotal)}</span>
            <AngleRightIcon className="social-bet-table__pill-chevron" />
          </>
        )}
      </button>

      {/* ── Social-focus TOP BAR (mobile, v18) — one tight compact row ──
          v17 REPLACE-THE-STREAM: the bets list FILLS the top region and the
          REAL <video> (owned by the page) shrinks via CSS into the top-right
          16:9 corner — NO canvas thumbnail. v18 compacts this into a SINGLE
          row: a small square back button, the live state readout
          (phase caption + climbing multiplier / timer) inline next to it, and
          the reserved 16:9 corner on the right. The corner video is itself a
          return-to-stream affordance; the square back button is the explicit
          one. ── */}
      <div className="social-bet-table__focus-bar">
        {/* LEFT: square back button + inline compact live state */}
        <button
          aria-label={t('Back to stream')}
          className="social-bet-table__focus-back"
          onClick={onCloseSocial}
          type="button"
        >
          <ArrowLeftIcon className="social-bet-table__focus-back-icon" />
        </button>
        <span className="social-bet-table__state">
          <span className="social-bet-table__state-phase">
            {phaseState === 'live' && <span className="social-bet-table__live-dot" />}
            {topBar?.phaseLabel ?? summaryLabel}
          </span>
          <span
            className="social-bet-table__state-value"
            ref={stateValueRef}
            data-heat={topBar?.stateHeat ?? 'cool'}
          >
            {topBar?.valueText ?? ''}
          </span>
        </span>

        {/* RIGHT: reserved 16:9 corner for the REAL cornered <video>
            (page-owned, position: fixed over this slot). A transparent tap
            target returns to the stream so tapping the corner video works.
            Now on EVERY game — the page's cornering class is no longer
            crash-gated either, so without this slot a marble player in social
            focus would get the overlay with no reserved space for the video
            to sit in. */}
        <button
          aria-label={t('Back to stream')}
          className="social-bet-table__stream-corner-slot"
          onClick={onCloseSocial}
          type="button"
        />
      </div>

      {/* ── Tab rail (v20): 'All bets' + the icon-only previous round.
          The money moved OUT of the tabs into the meter band below — tabs
          switch context, the meter tells the money story. ── */}
      <div className="social-bet-table__header">
        <div className="social-bet-table__tabs">
          {/* v26 — no wordmark: a slightly bigger pulsing LIVE bar carries
              the "this is the live round" signal (no translation-length
              issues); the label survives as the aria name. */}
          <button
            aria-label={t('All bets')}
            className={`social-bet-table__tab ${activeTab === 'current' ? 'social-bet-table__tab--active' : ''}`}
            onClick={() => onTabClick('current')}
            title={t('All bets')}
            type="button"
          >
            {/* The round-state lamp (v31): white while betting is open,
                amber for the final seconds (the hero's own 'warm' heat
                signal), green in flight, red on the visual crash — the
                phase classes on the root drive color; this modifier adds
                the betting-ending step. */}
            <span
              aria-hidden="true"
              className={`social-bet-table__live-bar${
                phaseState === 'betting' && topBar?.stateHeat === 'warm'
                  ? ' social-bet-table__live-bar--ending'
                  : ''
              }`}
            />
            <span className="social-bet-table__tab-count">{currentCount}</span>
          </button>
          <button
            aria-label={t('Previous round')}
            className={`social-bet-table__tab social-bet-table__tab--icon ${activeTab === 'previous' ? 'social-bet-table__tab--active' : ''}`}
            onClick={() => onTabClick('previous')}
            title={t('Previous round')}
            type="button"
          >
            <HistoryIcon className="social-bet-table__history-icon" />
            {previousCount > 0 && (
              <span className="social-bet-table__tab-count">{previousCount}</span>
            )}
          </button>
        </div>
        {meter && (
          <BetMeterBand
            crashLine={meterCrashLine}
            currencySymbol={currencySymbol}
            fmt={fmt}
            heroKind={heroKind}
            meter={meter}
            onNavBack={activeTab === 'previous' ? () => onTabClick('current') : null}
            onNavPrevious={activeTab === 'current' ? () => onTabClick('previous') : null}
            showProgress={showProgress}
            snapKey={meterSnapKey}
            t={t}
          />
        )}
      </div>

      {!isCollapsed && (
        /* Keyed by tab (v29): switching All bets ⇄ Previous remounts the
           list with a 140ms enter fade — the swap reads intentional. The
           remount also resets each row's flash guard cleanly (no flash on
           mount, by design). */
        <div
          className={`social-bet-table__list ${activeTab === 'current' ? 'social-bet-table__list--current' : ''}`}
          key={activeTab}
        >
          {sortedBets.length === 0 ? (
            /* Empty seats — three dimmed glass gems drifting gently, the
               same marbles players wear once they arrive (v26: visuals
               only, no caption — the copy lives on as the aria name).
               Reduced-motion stills them (CSS). The seed strings are chosen
               so the deterministic hash lands a spread teal/violet/gold
               trio (not three near-identical hues). */
            <div
              aria-label={activeTab === 'current' ? t('No bets yet') : t('No previous bets')}
              className="social-bet-table__empty"
              role="status"
            >
              <span aria-hidden="true" className="social-bet-table__empty-gems">
                <span className="social-bet-table__empty-gem">
                  <PlayerAvatar country={undefined} name="·seat·9" />
                </span>
                <span className="social-bet-table__empty-gem">
                  <PlayerAvatar country={undefined} name="·seat·17" />
                </span>
                <span className="social-bet-table__empty-gem">
                  <PlayerAvatar country={undefined} name="·spot·37" />
                </span>
              </span>
            </div>
          ) : (
            sortedBets.map((bet, index) => (
              <BetTableRow
                bet={bet}
                cascadeIndex={index}
                converted={converted}
                currency={currency}
                currencySymbol={currencySymbol}
                currentRoundCrashed={currentRoundCrashed}
                displayMultiplier={displayMultiplier}
                isBirdie={isBirdie}
                isCrashGame={isCrashGame}
                key={bet.id}
                liveBoard={liveBoard}
                marbles={marbles}
                previousRoundCancelled={previousRoundCancelled}
                tab={activeTab}
              />
            ))
          )}
        </div>
      )}

      {/* B22 — the balls' two gradients, declared ONCE for every Birdie pick
          in the list above (fixed ids, idempotent — `GolfBall.tsx`; the page
          mounts a copy for the board, this one keeps the panel whole on its
          own). A zero-sized, absolutely positioned svg: it takes no part in
          the panel's layout, and paint servers resolve at paint time, so
          after the balls is as good as before them. Nothing renders here on
          any other game — but the `false` still counts as a child, and every
          `useId` in the other games' rows (the avatars' gradient ids) encodes
          BOTH the slot index of each ancestor AND the bit length of each
          ancestor's child COUNT (`pushTreeContext`:
          `getBitLength(totalChildren)`). Two rules keep their markup
          byte-identical, then: the slot stays LAST, so no sibling's index
          moves; and the root keeps at most SEVEN children — c65b8085's four
          and today's five share a bit length, an eighth would shift every
          avatar id even if appended last. `socialFeedByteIdentity.test.ts`
          is the tripwire for both. */}
      {isBirdie && <BirdieBallDefs />}
    </div>
  )
}
